import React, {
  useEffect,
  useState,
  useRef,
  ChangeEvent,
  useCallback,
  useMemo,
} from 'react'
import './AudioWaveform.css'
import { TranscriptData } from '@dataTypes/message.types'
import InputField from '../InputField/InputField.component'
import AudioService from '../../../services/audio.service'
import { formatMinuteTime } from '@utils/time.util'
import AudioBarChart from './AudioBarChart.component'

import { logger } from '@utils/safeLogger'

interface AudioVisualizerProps {
  audioUrl: string
  defaultColor?: string
  disableSpeed?: boolean
  disableVolume?: boolean
  height?: number
  orientation: 'horizontal' | 'vertical'
  transcriptData: TranscriptData[]
  width?: number
}

function AudioWaveform({
  audioUrl,
  disableSpeed = false,
  disableVolume = false,
  height = 23.9,
  orientation,
  transcriptData,
  width,
}: AudioVisualizerProps): React.ReactElement {
  const completeTranscript = transcriptData?.map((item, index) => ({
    ...item,
    id: index,
  }))
  const speedSelection: string[] = useMemo(
    () => ['0.5x', '1.0x', '1.5x', '2.0x'],
    [],
  )
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [filteredTranscript, setFilteredTranscript] =
    useState<TranscriptData[]>(completeTranscript)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [activeTimeIndex, setActiveTimeIndex] = useState<number>()
  const [searchText, setSearchText] = useState<string>('')
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | null>(null)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [isLoadWaveform, setIsLoadWaveform] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [currentTime, setCurrentTime] = useState<number>(0)
  const [duration, setDuration] = useState<number>(0)
  const [speedIndex, setSpeedIndex] = useState<number>(1)
  const [volume, setVolume] = useState<number>(50)
  const [isMuted, setIsMuted] = useState<boolean>(false)
  const [isShowVolume, setIsShowVolume] = useState<boolean>(false)
  const transcriptContainerRef = useRef<HTMLDivElement | null>(null)
  const visualizerRef = useRef<HTMLDivElement | null>(null)
  const [actualWidth, setActualWidth] = useState<number>(width || 400)
  const barWidth = 2
  const barGap = 2
  const radius = 1

  const processAudioData = (
    _audioBuffer: AudioBuffer,
    logicalWidth: number,
  ): number[] => {
    if (_audioBuffer) {
      const rawData = _audioBuffer.getChannelData(0)
      const bufferLength = rawData.length

      // Calculate how many bars we can actually fit using logical width
      const totalBarWidth = barWidth + barGap
      const numBars = Math.floor(logicalWidth / totalBarWidth)
      const ratio = bufferLength / numBars
      const processedData: number[] = []

      if (ratio > 1) {
        // Downsampling: Use RMS (Root Mean Square) for better waveform representation
        for (let i = 0; i < numBars; i++) {
          const start = Math.floor(i * ratio)
          const end = Math.floor((i + 1) * ratio)
          const segment = rawData.slice(start, end)

          if (segment.length > 0) {
            // Calculate RMS value
            const sumSquares = segment.reduce(
              (sum, value) => sum + value * value,
              0,
            )
            const rms = Math.sqrt(sumSquares / segment.length)
            processedData.push(rms)
          } else {
            processedData.push(0)
          }
        }
      } else {
        // Upsampling: Use absolute value for consistency
        for (let i = 0; i < numBars; i++) {
          const index = Math.floor(i * ratio)
          processedData.push(Math.abs(rawData[index] || 0))
        }
      }
      return processedData
    }
    return []
  }

  const getActiveTimeIndex = useCallback(
    (currentSeconds: number): number => {
      if (!transcriptData) return 0
      for (let index = 0; index < transcriptData.length; index++) {
        const time = transcriptData[index].time
        const timeParts = time.split(':')
        const arrayLength = timeParts.length
        const seconds = parseInt(timeParts[arrayLength - 1])
        const minutes = parseInt(timeParts[arrayLength - 2])
        let hour = 0
        if (arrayLength === 3) hour = parseInt(timeParts[0])
        const totalSeconds = hour * 60 * 60 + minutes * 60 + seconds
        if (currentSeconds < totalSeconds) {
          return index === 0 ? index : index - 1
        }
      }
      return transcriptData.length - 1
    },
    [transcriptData],
  )

  const handlePlayButton = (): void => {
    const _isPlaying = !isPlaying
    if (audioRef.current) {
      if (_isPlaying) {
        void audioRef.current.play().then(
          () => setIsPlaying(true),
          () => setIsPlaying(false),
        )
      } else {
        audioRef.current.pause()
        setIsPlaying(false)
      }
    }
  }

  const handleVolumeButton = (): void => {
    const _isMuted = !isMuted
    if (audioRef.current) {
      setIsMuted(_isMuted)
      audioRef.current.muted = _isMuted
    }
  }

  const handleVolumeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newVolume = Number(e.target.value)
    setVolume(newVolume)
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100
    }
  }

  const handleSpeedChange = (): void => {
    const previousSpeedIndex = speedIndex
    if (previousSpeedIndex === speedSelection.length - 1) {
      setSpeedIndex(0)
    } else {
      setSpeedIndex(previousSpeedIndex + 1)
    }
  }

  const handleCanvasClick = (
    e: React.MouseEvent<HTMLCanvasElement, MouseEvent>,
  ): void => {
    if (audioRef.current && canvasRef.current && actualWidth > 0) {
      const canvas = canvasRef.current
      const rect = canvas.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const clickPositionRatio = clickX / actualWidth
      const newTime = clickPositionRatio * audioRef.current.duration
      audioRef.current.currentTime = newTime
    }
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    const text = e.target.value
    setSearchText(text)
    setFilteredTranscript(
      completeTranscript.filter(
        (data) =>
          data['message'].toLowerCase().includes(text.toLowerCase()) ||
          data['speaker'].toLowerCase().includes(text.toLowerCase()),
      ),
    )
  }

  const updateProgress = useCallback((): void => {
    const audio = audioRef.current
    const transcriptRef = transcriptContainerRef.current
    if (audio && audio.duration > 0) {
      setCurrentTime(audio.currentTime)
      setProgress((audio.currentTime / audio.duration) * 100)
      const index = getActiveTimeIndex(audio.currentTime)
      if (transcriptRef) {
        const activeElement = transcriptRef.querySelector('.active')
        if (activeElement) {
          activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      }
      setActiveTimeIndex(index)
    }
  }, [getActiveTimeIndex])

  const drawFullWaveform = useCallback((): void => {
    if (canvasRef.current && audioBuffer && actualWidth > 0) {
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d', { alpha: true })
      if (ctx) {
        // Get device pixel ratio for crisp rendering on high DPI displays
        const dpr = window.devicePixelRatio || 1

        // Set actual canvas size in memory (scaled by device pixel ratio)
        canvas.width = actualWidth * dpr
        canvas.height = height * dpr

        // Scale the canvas back down using CSS
        canvas.style.width = `${actualWidth}px`
        canvas.style.height = `${height}px`

        // Scale the drawing context so everything draws at the correct size
        ctx.scale(dpr, dpr)

        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'

        const processedData = processAudioData(audioBuffer, actualWidth)
        const color = '#65ACF5'

        // Find the maximum value for normalization
        const maxValue = Math.max(...processedData.map(Math.abs))
        const minBarHeight = 1.5 // Minimum bar height in pixels

        ctx.clearRect(0, 0, actualWidth, height)
        ctx.fillStyle = color

        processedData.forEach((value, index) => {
          const x = index * (barWidth + barGap)
          const normalizedValue = maxValue > 0 ? Math.abs(value) / maxValue : 0
          // Scale to use 80% of canvas height, with minimum bar height
          const barHeight = Math.max(
            minBarHeight,
            normalizedValue * height * 0.8,
          )
          const y = (height - barHeight) / 2

          ctx.beginPath()
          ctx['roundRect'](x, y, barWidth, barHeight, radius)
          ctx.fill()
          ctx.closePath()
        })
      }
    }
  }, [audioBuffer, height, actualWidth])

  const drawSeeker = useCallback((): void => {
    const canvas = canvasRef.current
    if (canvas && audioBuffer && actualWidth > 0) {
      const ctx = canvas.getContext('2d', { alpha: true })
      if (ctx) {
        // Get device pixel ratio for crisp rendering
        const dpr = window.devicePixelRatio || 1

        // Set actual canvas size in memory (scaled by device pixel ratio)
        canvas.width = actualWidth * dpr
        canvas.height = height * dpr

        // Scale the canvas back down using CSS
        canvas.style.width = `${actualWidth}px`
        canvas.style.height = `${height}px`

        // Scale the drawing context
        ctx.scale(dpr, dpr)

        // Enable image smoothing
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'

        // Draw the waveform
        const processedData = processAudioData(audioBuffer, actualWidth)
        const color = '#65ACF5'
        const maxValue = Math.max(...processedData.map(Math.abs))
        const minBarHeight = 1.5

        ctx.clearRect(0, 0, actualWidth, height)
        ctx.fillStyle = color

        processedData.forEach((value, index) => {
          const x = index * (barWidth + barGap)
          const normalizedValue = maxValue > 0 ? Math.abs(value) / maxValue : 0
          const barHeight = Math.max(
            minBarHeight,
            normalizedValue * height * 0.8,
          )
          const y = (height - barHeight) / 2

          ctx.beginPath()
          ctx['roundRect'](x, y, barWidth, barHeight, radius)
          ctx.fill()
          ctx.closePath()
        })

        // Draw the seeker on top
        const diamondSize = 5.657
        const seekerX = (progress / 100) * actualWidth
        ctx.fillStyle = '#185DD8'
        ctx.beginPath()
        ctx['roundRect'](
          seekerX - diamondSize / 4,
          0,
          diamondSize / 2,
          height,
          diamondSize / 2,
        )
        ctx.fill()
        ctx.closePath()

        ctx.fillStyle = '#185DD8'
        ctx.beginPath()
        ctx.moveTo(seekerX - diamondSize, 0)
        ctx.lineTo(seekerX + diamondSize, 0)
        ctx.lineTo(seekerX, diamondSize - 1)
        ctx.closePath()
        ctx.fill()
      }
    }
  }, [audioBuffer, progress, height, actualWidth])

  const highlightText = (text: string, highlight: string): React.ReactNode => {
    if (text.length && highlight.length) {
      const regExp = new RegExp(`(${highlight})`, 'gi')
      const parts = text.split(regExp)
      return parts.map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <span key={index} className="search-highlight">
            {part}
          </span>
        ) : (
          part
        ),
      )
    } else {
      return <span>{text}</span>
    }
  }

  const updateDuration = useCallback((): void => {
    if (audioRef.current && !duration) {
      if (audioRef.current.duration) {
        setDuration(audioRef.current.duration)
      }
    }
  }, [duration])

  const loadAudio = useCallback(async (): Promise<void> => {
    if (!audioUrl) return
    try {
      const decodedData = await AudioService.getAudioBuffer(audioUrl)
      setAudioBuffer(decodedData)
      setDuration(audioRef.current?.duration || 0)
    } catch (error) {
      logger.error('Failed to decode call recording for waveform', error)
    }
  }, [audioUrl])

  useEffect(() => {
    setIsLoadWaveform(false)
    setAudioBuffer(null)
    setProgress(0)
    setCurrentTime(0)
    setDuration(0)
    setIsPlaying(false)
    audioRef.current?.load()
  }, [audioUrl])

  useEffect(() => {
    loadAudio()
    const audioElement = audioRef.current
    audioElement?.addEventListener('timeupdate', updateProgress)
    audioElement?.addEventListener('loadedmetadata', updateDuration)

    return () => {
      audioElement?.removeEventListener('timeupdate', updateProgress)
      audioElement?.removeEventListener('loadedmetadata', updateDuration)
    }
  }, [loadAudio, updateProgress, updateDuration])

  useEffect(() => {
    if (audioRef.current) {
      if (
        formatMinuteTime(audioRef.current.duration) ===
          formatMinuteTime(currentTime) &&
        currentTime != 0
      ) {
        setIsPlaying(false)
        setActiveTimeIndex(undefined)
      }
    }
  }, [audioRef, currentTime])

  // Measure container width and update actualWidth
  useEffect(() => {
    const updateWidth = (): void => {
      if (visualizerRef.current) {
        const containerWidth = visualizerRef.current.clientWidth
        // Account for padding and margins (16px margin-left from CSS)
        const availableWidth = Math.max(100, containerWidth - 16)
        setActualWidth(availableWidth)
      } else if (width !== undefined) {
        setActualWidth(width)
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [width])

  useEffect(() => {
    if (duration && audioBuffer && !isLoadWaveform) {
      drawFullWaveform()
      setIsLoadWaveform(true)
    }
  }, [duration, audioBuffer, isLoadWaveform, drawFullWaveform, actualWidth])

  useEffect(() => {
    drawSeeker()
  }, [progress, drawSeeker])

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = parseFloat(speedSelection[speedIndex])
    }
  }, [speedIndex, speedSelection])

  return (
    <>
      {duration > 0 && transcriptData && transcriptData.length > 0 && (
        <AudioBarChart transcriptData={transcriptData} duration={duration} />
      )}
      <div className="audio-waveform-container">
        <div className="play-container">
          <button
            type="button"
            aria-label={isPlaying ? 'Pause' : 'Play'}
            onClick={handlePlayButton}
            className={`unstyled-button ti ti-player-${isPlaying ? 'pause' : 'play'}-filled play-btn`}
          />
          <div className="time-progress-display">
            <span className="current-time">
              {formatMinuteTime(currentTime)}/
            </span>
            <span className="total-duration">
              {formatMinuteTime(audioRef.current?.duration || 0)}
            </span>
          </div>
          <div className="audio-visualizer" ref={visualizerRef}>
            <canvas ref={canvasRef} onClick={handleCanvasClick} />
          </div>
          <audio
            ref={audioRef}
            controls={false}
            className="audio-player"
            src={audioUrl || undefined}
          />
        </div>
        {disableVolume ? null : (
          <div
            className="volume-container"
            onMouseEnter={() => setIsShowVolume(true)}
            onMouseLeave={() => setIsShowVolume(false)}
          >
            <i
              onClick={handleVolumeButton}
              className={`mgc_volume${isMuted ? '_mute' : ''}_fill vol-btn`}
            ></i>
            {isShowVolume ? (
              <input
                className={`volume-control-${orientation}`}
                onMouseEnter={() => setIsShowVolume(true)}
                onMouseLeave={() => setIsShowVolume(false)}
                disabled={isMuted}
                type="range"
                min="0"
                max="100"
                step="1"
                value={volume}
                onChange={handleVolumeChange}
              />
            ) : null}
          </div>
        )}
        {disableSpeed ? null : (
          <span onClick={handleSpeedChange} className="speed-btn">
            {speedSelection[speedIndex]}
          </span>
        )}
      </div>

      <div className="transcript-row">
        <div className="d-flex flex-row-reverse align-items-end w-100">
          <InputField
            placeholder="Search"
            value={searchText}
            leftIcon={<i className="fa-solid fa-magnifying-glass"></i>}
            onChange={handleSearch}
            className="audio-transcript-search-bar"
          />
        </div>

        <div ref={transcriptContainerRef} className="audio-text-container">
          {!filteredTranscript || filteredTranscript.length === 0 ? (
            <span className="custom-filter-subheader mt-3 fs-14 fw-medium m-0 text-center">
              Transcription not available
            </span>
          ) : (
            filteredTranscript.map((data, index) => {
              return (
                <div
                  key={index}
                  className={`message-container ${data.id === activeTimeIndex ? 'active' : 'inactive'}`}
                >
                  <div className="d-flex w-100 justify-content-between">
                    <span className={`${data.speaker.toLowerCase()}-speaker`}>
                      {highlightText(data.speaker, searchText)}
                    </span>
                    <span className="time">[{data.time}]</span>
                  </div>
                  <div className={`message ${data.speaker.toLowerCase()}`}>
                    {highlightText(data.message, searchText)}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </>
  )
}

export default React.memo(AudioWaveform)
